import React from "react";
import {Box, Button, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import {grey} from "@mui/material/colors";
import Close from "@mui/icons-material/Close";
import Edit from "@mui/icons-material/Edit";
import {useNavigate} from "react-router-dom";
import {ElevationApp} from "../../elevationTool/ElevationApp";
import {EventEmitter2} from "../../elevationTool/util/EventEmitter2";
import {useEventEmitter2} from "../../elevationTool/util/useEventEmitter2";
import {selectionTitle} from "../../elevationTool/menubar/MenuBar";
import {MdDisplayView} from "../../elevationTool/model/view/MdViewSettings";
import {elevationToolUrl} from "./ElevationSidebarContext";
import {SidebarTab, sidebarTabLabel, sidebarTabWorkspace} from "./useSidebarElevationApp";
import {SelectionDetails} from "./SelectionDetails";

// the strip under the sidebar's tabs: the opening and the tab's elevation name, or the details
// of the element selected on the drawing, and the Edit button into the elevation tool.
export function ElevationSidebarDetails(props: {
    app: EventEmitter2<ElevationApp | null>;
    tab: SidebarTab;
    project: number;
    alternative?: number;
    openingId: number;
    openingName: string;
}) {
    const app = useEventEmitter2(props.app);
    const ready = useEventEmitter2(app?.ready);
    const selection = useEventEmitter2(app?.viewSettings.selection) ?? [];
    const names = useEventEmitter2(app?.elevationNames);
    const activeView = useEventEmitter2(app?.viewSettings.activeView);
    const secondaryOf = useEventEmitter2(app?.secondaryOf);
    const navigate = useNavigate();

    const loaded = !!app && !!ready && !app.loadError;
    const hasSelection = loaded && selection.length > 0;
    const workspace = sidebarTabWorkspace(props.tab);

    const elevationName = names?.[workspace] ?? null;
    const subtitle = !loaded ? "" : props.tab === "screen" && secondaryOf
        ? `Frame on ${secondaryOf.primaryName || "another opening"}`
        : elevationName ?? "Default (not saved)";

    const editUrl = elevationToolUrl({
        project: props.project,
        alternative: props.alternative,
        openingId: props.openingId,
        workspace,
        anchors: props.tab === "anchors",
    });

    // the pair's leaf the door / locations view shows.
    const leafViews = leafViewsFor(props.tab);
    const showLeaf = loaded && !!app.frameSpec.isPair && !!leafViews && !!activeView && (activeView === leafViews.active || activeView === leafViews.inactive);

    return (
        <Box sx={{px: 1, py: 0.75, borderBottom: "1px solid " + grey["300"], flexShrink: 0}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography sx={{fontSize: 13, fontWeight: 600, lineHeight: 1.3}} noWrap>
                        {hasSelection ? `Selected ${selectionTitle(app, selection)}` : props.openingName || `Opening ${props.openingId}`}
                    </Typography>
                    <Typography sx={{fontSize: 11, color: "text.secondary", lineHeight: 1.3}} noWrap>
                        {hasSelection ? props.openingName : subtitle}
                    </Typography>
                </Box>
                {hasSelection && (
                    <Tooltip title="Clear the selection">
                        <IconButton size="small" onClick={() => app.viewSettings.clearSelection()}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {showLeaf && leafViews && (
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={activeView}
                        onChange={(e, value: MdDisplayView | null) => value && app.showView(value)}
                        sx={{"& .MuiToggleButton-root": {py: 0, px: 1, fontSize: 11, textTransform: "none"}}}
                    >
                        <ToggleButton value={leafViews.active}>Active</ToggleButton>
                        <ToggleButton value={leafViews.inactive}>Inactive</ToggleButton>
                    </ToggleButtonGroup>
                )}
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => navigate(editUrl)}
                    sx={{flexShrink: 0, whiteSpace: "nowrap"}}
                >
                    Edit {sidebarTabLabel(props.tab)}
                </Button>
            </Box>
            {hasSelection ? (
                <SelectionDetails app={app} selection={selection} />
            ) : loaded && (
                <Typography sx={{fontSize: 11, color: "text.disabled", pt: 0.5}}>
                    {props.tab === "anchors"
                        ? "Click an outside stick to see its anchors."
                        : "Click an element in the drawing to see its details."}
                </Typography>
            )}
        </Box>
    );
}

function leafViewsFor(tab: SidebarTab): {active: MdDisplayView; inactive: MdDisplayView} | null {
    switch(tab) {
        case "door": return {active: "door-active", inactive: "door-inactive"};
        case "hardware": return {active: "location-active", inactive: "location-inactive"};
        default: return null;
    }
}
